/**
 * Modal Registry
 *
 * Maps modal types to loaders and behavior (wizard, renderOwnOverlay).
 * Modals are loaded on first open via dynamic import; ModalContainer uses no hard-coded types.
 * MODAL_TYPES is derived from registry keys so the registry is the single source of truth.
 *
 * One type per component. Pass context explicitly when opening:
 * - ADD_SERVICE: data: { orderId } for "add to order", omit for standalone
 * - ACCOUNT_CODE_FORM: data: { accountCode } for edit, omit for add
 * - ADD_USER: data: { user } for edit, omit for add
 */

import React from "react";
import { MODAL_SIZE } from "@constants/appDefaults";

/** @typedef {{ loader: () => Promise<{ default: React.ComponentType }>, wizard?: boolean, renderOwnOverlay?: boolean, metadata?: { titleKey?: string, size?: string, totalSteps?: number, stepLabelKeys?: string[], disableBackdropClose?: boolean } }} ModalEntry */

/**
 * Modal Registry: modalType -> { loader, wizard?, renderOwnOverlay?, metadata? }
 * - metadata.titleKey: i18n key for modal title (resolved in ModalContainer so titles are always UX-friendly).
 * - metadata: optional { titleKey, size, totalSteps, stepLabelKeys, disableBackdropClose }; stepLabelKeys are i18n keys resolved with t() in getModalMetadata
 */
export const modalRegistry = {
  // Order Modals
  ADD_ORDER: {
    loader: () => import("@components/Modals/OrderModals/AddOrderModal"),
    wizard: true,
    metadata: {
      titleKey: "addOrder",
      totalSteps: 3,
      stepLabelKeys: ["orders.customerInfo", "orders.services", "orders.review"],
      size: MODAL_SIZE.LARGE,
    },
  },
  GENERATE_INVOICE: {
    loader: () => import("@components/Modals/OrderModals/GenerateInvoiceModal"),
    metadata: { titleKey: "moreButton.generateInvoice" },
  },
  EDIT_SERVICE: {
    loader: () => import("@components/Modals/OrderModals/EditServiceModal"),
    metadata: { titleKey: "editService" },
  },
  EDIT_COST: { loader: () => import("@components/Modals/OrderModals/EditCostModal"), metadata: { titleKey: "editService" } },
  ADD_SERVICE: { loader: () => import("@components/Modals/ServiceModals/AddServiceModal"), metadata: { titleKey: "addService" } },
  EDIT_CUSTOMER_IN_ORDER: {
    loader: () => import("@components/Modals/OrderModals/EditCustomerInOrderModal"),
    metadata: { titleKey: "editCustomer" },
  },

  // Customer Modals
  ADD_CUSTOMER: { loader: () => import("@components/Modals/CustomerModals/AddCustomerModal"), metadata: { titleKey: "addCustomer" } },
  EDIT_CUSTOMER: { loader: () => import("@components/Modals/CustomerModals/EditCustomerModal"), metadata: { titleKey: "editCustomer" } },
  MAKE_ORDER: {
    loader: () => import("@components/Modals/CustomerModals/MakeOrderModal"),
    wizard: true,
    metadata: { titleKey: "addOrder" },
  },
  CUSTOMER_DETAILS: {
    loader: () => import("@components/Modals/CustomerModals/CustomerDetailsModal"),
    metadata: { titleKey: "customerDetails" },
  },

  // Invoice Modals
  MAKE_INVOICE: {
    loader: () => import("@components/Modals/InvoiceModals/MakeInvoiceModal"),
    wizard: true,
    metadata: {
      titleKey: "addOrder",
      totalSteps: 2,
      stepLabelKeys: ["orders.customerInfo", "orders.services"],
      size: MODAL_SIZE.LARGE,
    },
  },
  EDIT_INVOICE: {
    loader: () => import("@components/Modals/InvoiceModals/EditInvoiceModal"),
    wizard: true,
    metadata: {
      titleKey: "invoice.edit_invoice",
      totalSteps: 2,
      stepLabelKeys: ["orders.customerInfo", "orders.services"],
      size: MODAL_SIZE.LARGE,
    },
  },
  PDF_VIEWER: { loader: () => import("@components/Modals/InvoiceModals/PdfViewerModal"), metadata: { titleKey: "invoice.mark_services" } },

  // Service Modals
  EDIT_SERVICE_ITEM: {
    loader: () => import("@components/Modals/ServiceModals/EditServiceItemModal"),
    metadata: { titleKey: "editService" },
  },
  SERVICE_DETAILS: {
    loader: () => import("@components/Modals/ServiceModals/ServiceDetailsModal"),
    metadata: { titleKey: "editService" },
  },

  // Expense Modals (ADD_EXPENSE and EDIT_EXPENSE use same component; edit when data.expense or data.item)
  ADD_EXPENSE: { loader: () => import("@components/Modals/ExpenseModals/AddExpenseModal"), metadata: { titleKey: "addExpences" } },
  EDIT_EXPENSE: { loader: () => import("@components/Modals/ExpenseModals/AddExpenseModal"), metadata: { titleKey: "editExpences" } },

  // Investment Modals
  ADD_INVESTMENT: { loader: () => import("@components/Modals/InvestmentModals/AddInvestmentModal"), metadata: { titleKey: "addInvestment" } },
  EDIT_INVESTMENT: { loader: () => import("@components/Modals/InvestmentModals/EditInvestmentModal"), metadata: { titleKey: "editInvestment" } },

  // Category Modals
  ADD_SERVICE_TYPE: { loader: () => import("@components/Modals/CategoryModals/AddServiceTypeModal"), metadata: { titleKey: "addServiceType" } },
  EDIT_SERVICE_TYPE: { loader: () => import("@components/Modals/CategoryModals/EditServiceTypeModal"), metadata: { titleKey: "editServiceType" } },
  ADD_EXPENSE_CATEGORY: { loader: () => import("@components/Modals/CategoryModals/AddExpenseCategoryModal"), metadata: { titleKey: "addExpenceCategory" } },
  EDIT_EXPENSE_CATEGORY: { loader: () => import("@components/Modals/CategoryModals/EditExpenseCategoryModal"), metadata: { titleKey: "editExpenseCategory" } },
  ADD_INVESTMENT_CATEGORY: { loader: () => import("@components/Modals/CategoryModals/AddInvestmentCategoryModal"), metadata: { titleKey: "addInvestmentCategory" } },
  EDIT_INVESTMENT_CATEGORY: { loader: () => import("@components/Modals/CategoryModals/EditInvestmentCategoryModal"), metadata: { titleKey: "editInvestmentCategory" } },
  ADD_SERVICE_COMPANY: { loader: () => import("@components/Modals/CategoryModals/AddServiceCompanyModal"), metadata: { titleKey: "addServiceCompany" } },
  EDIT_SERVICE_COMPANY: { loader: () => import("@components/Modals/CategoryModals/AddServiceCompanyModal"), metadata: { titleKey: "editServiceCompany" } },

  // Display Modals (add: no data; edit: data: { accountCode })
  ACCOUNT_CODE_FORM: { loader: () => import("@components/Modals/DisplayModals/AccountCodeFormModal"), metadata: { titleKey: "editAccountCode" } },

  // User Modals (add: no data; edit: data: { user })
  ADD_USER: { loader: () => import("@components/Modals/UserModals/AddUserModal"), metadata: { titleKey: "addUser" } },

  // Confirmation Modals (renders its own overlay)
  CONFIRM_DELETE: { loader: () => import("@components/Modals/ConfirmModal/ConfirmModal"), renderOwnOverlay: true },


};

/** Canonical modal type constants; derived from registry keys so the registry stays the single source of truth. */
export const MODAL_TYPES = Object.freeze(
  Object.fromEntries(Object.keys(modalRegistry).map((k) => [k, k]))
);

/**
 * Get modal component for a given type (lazy when entry has loader)
 *
 * @param {string} modalType - The modal type constant
 * @returns {React.ComponentType|null} - The component or null if not registered
 */
export const getModalComponent = (modalType) => {
  const entry = modalRegistry[modalType];
  if (!entry || !entry.loader) return null;
  if (!entry.Component) {
    entry.Component = React.lazy(entry.loader);
  }
  return entry.Component;
};

/**
 * Get modal behavior for a given type (wizard, renderOwnOverlay).
 *
 * @param {string} modalType - The modal type constant
 * @returns {{ wizard: boolean, renderOwnOverlay: boolean }}
 */
export const getModalBehavior = (modalType) => {
  const entry = modalRegistry[modalType];
  if (!entry || typeof entry !== "object") {
    return { wizard: false, renderOwnOverlay: false };
  }
  return {
    wizard: !!entry.wizard,
    renderOwnOverlay: !!entry.renderOwnOverlay,
  };
};

/**
 * Resolve registry metadata for a modal type. stepLabelKeys are translated with t();
 * use in ModalContainer so wizard size/totalSteps/stepLabels come from registry by default.
 *
 * @param {string} modalType - The modal type constant
 * @param {(key: string) => string} [t] - i18n translate function (e.g. from useTranslation)
 * @returns {{ titleKey?: string, size?: string, totalSteps?: number, stepLabels?: string[], disableBackdropClose?: boolean }}
 */
export const getModalMetadata = (modalType, t) => {
  const entry = modalRegistry[modalType];
  const meta = entry?.metadata;
  if (!meta || typeof meta !== "object") return {};
  const stepLabels =
    Array.isArray(meta.stepLabelKeys) && typeof t === "function"
      ? meta.stepLabelKeys.map((key) => t(key, key))
      : [];
  return {
    ...(meta.titleKey != null && { titleKey: meta.titleKey }),
    ...(meta.size != null && { size: meta.size }),
    ...(meta.totalSteps != null && { totalSteps: meta.totalSteps }),
    ...(stepLabels.length > 0 && { stepLabels }),
    ...(meta.disableBackdropClose != null && { disableBackdropClose: meta.disableBackdropClose }),
  };
};
